/**
 * Talent Cloud — Creator Workspace
 * Tabbed mini-CRM for managing one assigned creator.
 * Tabs: Overview · Media Kit · Pricing · Availability · Notes · Campaigns
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Sidebar } from '../../components/Sidebar';
import { FileUpload } from '../../components/FileUpload';
import { FileList } from '../../components/FileList';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { ChevronLeft, Instagram, Youtube, IndianRupee, BadgeCheck, FileText, DollarSign, Calendar, StickyNote, Briefcase, Award, Trash2, Save, Handshake, ShieldCheck, CheckSquare, Library, Plus, X, ExternalLink, Wallet } from 'lucide-react';
import { CreatorEarningsPanel } from '../../components/creator/CreatorEarningsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n);

const TABS = [
  { key: 'overview', label: 'Overview', icon: Award },
  { key: 'media', label: 'Media Kit', icon: FileText },
  { key: 'pricing', label: 'Rate Card', icon: DollarSign },
  { key: 'availability', label: 'Availability', icon: Calendar },
  { key: 'exclusivity', label: 'Exclusivity', icon: ShieldCheck },
  { key: 'negotiations', label: 'Negotiations', icon: Handshake },
  { key: 'tasks', label: 'Tasks', icon: CheckSquare },
  { key: 'content', label: 'Content Library', icon: Library },
  { key: 'notes', label: 'Notes', icon: StickyNote },
  { key: 'campaigns', label: 'Campaigns', icon: Briefcase },
  { key: 'earnings', label: 'Earnings', icon: Wallet },
];

const AVAILABILITY_OPTIONS = [
  { value: 'available', label: 'Available', color: '#00C972', desc: 'Open to brand campaigns' },
  { value: 'booked', label: 'Booked', color: '#FF451A', desc: 'Actively working on campaigns' },
  { value: 'vacation', label: 'Vacation', color: '#FFB800', desc: 'Temporarily unavailable' },
  { value: 'paused', label: 'Paused', color: '#999999', desc: 'Profile paused — no new outreach' },
  { value: 'locked', label: 'Exclusive Lock', color: '#9B6EFF', desc: 'Locked in exclusive campaign' },
];

export const TalentCreatorWorkspace = () => {
  const { id } = useParams();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [filesRefresh, setFilesRefresh] = useState(0);

  const fetchCreator = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/talent/creators/${id}`, { withCredentials: true });
      setCreator(data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Could not load creator');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCreator(); /* eslint-disable-next-line */ }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-screen"><Sidebar /><div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]"><div className="text-center py-20"><div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div></div></div></div>
    );
  }
  if (!creator) {
    return (
      <div className="flex min-h-screen"><Sidebar /><div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]"><div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center"><h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-2">Creator not found in your roster</h3><Link to="/talent/roster" className="text-[#FF451A] font-medium">← Back to roster</Link></div></div></div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 bg-white dark:bg-[#0A0B0D]" data-testid="talent-creator-workspace">
        <div className="max-w-6xl mx-auto p-8">
          <Link to="/talent/roster" className="inline-flex items-center gap-1 text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] mb-4">
            <ChevronLeft size={14} /> Back to roster
          </Link>

          {/* Hero */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-[#111111] to-[#1a1a1a] rounded-2xl p-8 text-[#0F0F0F] dark:text-white relative overflow-hidden mb-6">
            <div className="absolute -right-10 -top-10 w-60 h-60 rounded-full bg-[#FF451A]/30 blur-3xl"></div>
            <div className="relative z-10 flex items-start gap-6 flex-wrap">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#FF451A] to-[#9B6EFF] flex items-center justify-center text-3xl font-bold shrink-0">
                {creator.profile_photo_url ? <img src={creator.profile_photo_url} alt={creator.name} className="w-full h-full rounded-2xl object-cover" /> : creator.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h1 className="text-3xl font-bold tracking-tight">{creator.name}</h1>
                  <span className="px-2 py-0.5 rounded-full bg-[#FF451A]/20 border border-[#FF451A]/40 text-[#FF451A] text-xs font-semibold flex items-center gap-1 font-mono uppercase tracking-wider"><Award size={10} /> Managed</span>
                  <BadgeCheck size={18} className="text-[#0055FF]" />
                </div>
                {creator.instagram_handle && <p className="text-sm opacity-80 flex items-center gap-1"><Instagram size={12} /> @{creator.instagram_handle}</p>}
                {creator.bio && <p className="text-sm opacity-80 mt-2 max-w-xl">{creator.bio}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 relative z-10">
              <HeroStat label="IG Followers" value={fmtNum(creator.instagram_followers || 0)} />
              <HeroStat label="YT Subs" value={fmtNum(creator.youtube_subscribers || 0)} />
              <HeroStat label="Engagement" value={`${(creator.instagram_engagement || 0).toFixed(1)}%`} />
              <HeroStat label="Reel Price" value={`₹${fmtNum(creator.pricing_reel || 0)}`} />
            </div>
          </motion.div>

          {/* Tabs */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden">
            <div className="flex flex-wrap border-b border-black/[0.08] dark:border-white/5 bg-black/30 px-2 pt-2">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = activeTab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-all ${active ? 'text-[#0F0F0F] dark:text-white border-[#FF451A]' : 'text-[#6B6B6B] dark:text-zinc-500 border-transparent hover:text-zinc-300'}`}
                    data-testid={`tab-${t.key}`}
                  >
                    <Icon size={14} /> {t.label}
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {activeTab === 'overview' && <OverviewTab creator={creator} />}
              {activeTab === 'media' && (
                <div>
                  <FileUpload bucket="creator-media-kits" attachedTo="creator" attachedId={id} accept="application/pdf,image/*" maxBytes={10 * 1024 * 1024} label="Upload media kit (PDF/image, max 10MB)" onUploaded={() => setFilesRefresh((n) => n + 1)} />
                  <div className="mt-4">
                    <FileList attachedTo="creator" attachedId={id} refreshKey={filesRefresh} canDelete onDeleted={() => setFilesRefresh((n) => n + 1)} />
                  </div>
                </div>
              )}
              {activeTab === 'pricing' && <PricingTab creator={creator} onSaved={fetchCreator} />}
              {activeTab === 'availability' && <AvailabilityTab creator={creator} onSaved={fetchCreator} />}
              {activeTab === 'exclusivity' && <ExclusivityTab creatorId={id} />}
              {activeTab === 'negotiations' && <NegotiationsTab creatorId={id} />}
              {activeTab === 'tasks' && <TasksTab creatorId={id} />}
              {activeTab === 'content' && <ContentLibraryTab creatorId={id} />}
              {activeTab === 'notes' && <NotesTab creatorId={id} />}
              {activeTab === 'campaigns' && <CampaignsTab creatorId={id} />}
              {activeTab === 'earnings' && <CreatorEarningsPanel creatorId={id} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroStat = ({ label, value }) => (
  <div className="bg-white/5 rounded-xl p-3 backdrop-blur-sm border border-black/[0.10] dark:border-white/10">
    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-400 mb-1">{label}</div>
    <div className="text-2xl font-bold">{value}</div>
  </div>
);

const OverviewTab = ({ creator }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Email" value={creator.email} />
      <Field label="Quality grade" value={creator.quality_grade || '—'} />
      <Field label="Gender" value={creator.gender || '—'} />
      <Field label="Cities" value={(creator.cities || []).join(', ') || '—'} />
      <Field label="Languages" value={(creator.languages || []).join(', ') || '—'} />
      <Field label="Categories" value={(creator.categories || []).join(', ') || '—'} />
      <Field label="Tags" value={(creator.tags || []).join(', ') || '—'} className="md:col-span-2" />
      {creator.bio && <Field label="Bio" value={creator.bio} className="md:col-span-2" />}
    </div>
    <InternalProfileSection creator={creator} />
  </div>
);

// ---------------------------------------------------------------------------
// Internal Profile section — visible only inside TM/AE/Admin workspaces
// (this component is rendered from TalentCreatorWorkspace which is already
// gated to talent_manager / admin roles server-side). Every field defaults
// to "—" when empty; suspicious rows show a red Flagged pill.
// ---------------------------------------------------------------------------
const YesNoChip = ({ value }) => {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-[#00C972]/15 text-[#00C972] border border-[#00C972]/30">
        Yes
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30">
        No
      </span>
    );
  }
  return <span className="text-[#6B6B6B] dark:text-zinc-500">—</span>;
};

const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

const InternalProfileSection = ({ creator }) => (
  <div className="border-t border-black/[0.08] dark:border-white/5 pt-6" data-testid="internal-profile-section">
    <div className="flex items-center gap-2 mb-3">
      <ShieldCheck size={16} className="text-[#FF451A]" />
      <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#0F0F0F] dark:text-white">
        Internal Profile
      </h3>
      <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">
        TM / AE / Admin only
      </span>
      {creator.is_suspicious && (
        <span
          className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30"
          data-testid="internal-suspicious-flag"
        >
          <X size={10} /> Flagged
        </span>
      )}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Hair Colour Current"       value={dash(creator.hair_color_current)} />
      <Field label="Hair Colour Preference"    value={dash(creator.hair_color_preference)} />
      <Field label="Comfortable with Colouring" value={<YesNoChip value={creator.hair_coloring_comfortable} />} />
      <Field label="Pre-lightening Comfortable" value={<YesNoChip value={creator.pre_lightening_comfortable} />} />
      <Field label="Hair Type"                 value={dash(creator.hair_type)} />
      <Field label="Relevant Brands"           value={dash(creator.relevant_brands)} />
      <Field label="Manager Commission"        value={dash(creator.manager_commission)} />
      <Field label="Address"                   value={dash(creator.address)} className="md:col-span-2" />
      <Field
        label="Email (internal contact)"
        value={
          creator.email ? (
            <a
              href={`mailto:${creator.email}`}
              className="text-[#FF451A] hover:underline inline-flex items-center gap-1"
              data-testid="internal-email-link"
            >
              {creator.email} <ExternalLink size={11} />
            </a>
          ) : '—'
        }
      />
      <Field
        label="Snapchat"
        value={
          creator.snapchat ? (
            /^https?:\/\//i.test(creator.snapchat) ? (
              <a
                href={creator.snapchat}
                target="_blank"
                rel="noreferrer"
                className="text-[#FFB800] hover:underline inline-flex items-center gap-1"
                data-testid="internal-snapchat-link"
              >
                Open profile <ExternalLink size={11} />
              </a>
            ) : (
              <span data-testid="internal-snapchat-handle">{creator.snapchat}</span>
            )
          ) : '—'
        }
      />
    </div>
  </div>
);

const Field = ({ label, value, className = '' }) => (
  <div className={`bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4 ${className}`}>
    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-1">{label}</div>
    <div className="text-sm text-[#0F0F0F] dark:text-white break-words">{value}</div>
  </div>
);

const PricingTab = ({ creator, onSaved }) => {
  const { id } = useParams();
  const [form, setForm] = useState({
    pricing_reel: creator.pricing_reel || 0,
    pricing_story: creator.pricing_story || 0,
    pricing_integration: creator.pricing_integration || 0,
    pricing_long_form: creator.pricing_long_form || 0,
    pricing_ugc: creator.pricing_ugc || 0,
    pricing_whitelist: creator.pricing_whitelist || 0,
    pricing_event_appearance: creator.pricing_event_appearance || 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/talent/creators/${id}/pricing`, form, { withCredentials: true });
      toast.success('Rate card updated');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'pricing_reel', label: 'Reel', desc: '30s–90s Reels / TikToks' },
    { key: 'pricing_story', label: 'Story', desc: 'Single Instagram story (1-3 frames)' },
    { key: 'pricing_integration', label: 'Integration', desc: 'YouTube integration (60–120s)' },
    { key: 'pricing_long_form', label: 'Long Form', desc: 'Dedicated YouTube video' },
    { key: 'pricing_ugc', label: 'UGC', desc: 'Brand-owned creator content (no posting)' },
    { key: 'pricing_whitelist', label: 'Whitelisting', desc: 'Allow brand to boost from creator handle' },
    { key: 'pricing_event_appearance', label: 'Event Appearance', desc: 'Physical event / launch attendance' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.key} className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-1">{f.label}</div>
            <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 mb-2">{f.desc}</div>
            <div className="flex items-center gap-2">
              <IndianRupee size={14} className="text-[#6B6B6B] dark:text-zinc-500" />
              <Input
                type="number"
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: parseInt(e.target.value || '0', 10) })}
                className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid={`pricing-${f.key}-input`}
              />
            </div>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-6" data-testid="save-pricing-button">
        <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save Rate Card'}
      </Button>
    </div>
  );
};

const AvailabilityTab = ({ creator, onSaved }) => {
  const { id } = useParams();
  const [status, setStatus] = useState(creator.availability_status || 'available');
  const [note, setNote] = useState(creator.availability_note || '');
  const [blockedDates, setBlockedDates] = useState(creator.blocked_dates || []);
  const [newBlock, setNewBlock] = useState({ start: '', end: '', reason: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/talent/creators/${id}/availability`, {
        availability_status: status,
        availability_note: note,
        blocked_dates: blockedDates,
      }, { withCredentials: true });
      toast.success('Availability updated');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const addBlock = () => {
    if (!newBlock.start || !newBlock.end) {
      toast.error('Start and end dates required');
      return;
    }
    setBlockedDates([...blockedDates, newBlock]);
    setNewBlock({ start: '', end: '', reason: '' });
  };

  const removeBlock = (i) => setBlockedDates(blockedDates.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {AVAILABILITY_OPTIONS.map((opt) => {
          const active = status === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setStatus(opt.value)}
              className={`text-left p-4 rounded-xl border-2 transition-all ${active ? 'border-[#FF451A] bg-[#FF451A]/10' : 'border-black/[0.08] dark:border-white/5 bg-black/30 hover:border-white/10'}`}
              data-testid={`availability-${opt.value}`}
            >
              <div className="font-display text-lg" style={{ color: opt.color }}>{opt.label}</div>
              <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 mt-1">{opt.desc}</div>
            </button>
          );
        })}
      </div>

      <div>
        <label className="font-mono text-[9px] uppercase tracking-[0.2em] block mb-1 text-[#0F0F0F] dark:text-white">Note (optional)</label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="e.g. Back on Aug 15. Soft-block all skincare." className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="availability-note-input" />
      </div>

      {/* Blocked dates */}
      <div className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-3">Blocked Dates</div>
        <div className="space-y-2 mb-3">
          {blockedDates.length === 0 ? (
            <p className="text-xs text-[#A1A1AA] dark:text-zinc-600 italic">No blocked dates yet.</p>
          ) : blockedDates.map((b, i) => (
            <div key={i} className="flex items-center gap-3 bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg p-2.5 text-sm" data-testid="blocked-date-row">
              <Calendar size={12} className="text-[#FFB800]" />
              <span className="text-[#0F0F0F] dark:text-white font-mono">{b.start} → {b.end}</span>
              {b.reason && <span className="text-[#6B6B6B] dark:text-zinc-400">· {b.reason}</span>}
              <button onClick={() => removeBlock(i)} className="ml-auto text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30]"><X size={14} /></button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Input type="date" value={newBlock.start} onChange={(e) => setNewBlock({ ...newBlock, start: e.target.value })} className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="block-start-input" />
          <Input type="date" value={newBlock.end} onChange={(e) => setNewBlock({ ...newBlock, end: e.target.value })} className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="block-end-input" />
          <Input value={newBlock.reason} onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })} placeholder="Reason (optional)" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="block-reason-input" />
          <Button onClick={addBlock} className="bg-white/10 hover:bg-white/15 text-[#0F0F0F] dark:text-white rounded-lg" data-testid="add-block-button"><Plus size={12} className="mr-1" /> Add</Button>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-6" data-testid="save-availability-button">
        <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save Availability'}
      </Button>
    </div>
  );
};

const ExclusivityTab = ({ creatorId }) => {
  const [data, setData] = useState({ brand_exclusivity: [], category_exclusivity: [], exclusivity_expiry_date: '' });
  const [brandInput, setBrandInput] = useState('');
  const [catInput, setCatInput] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/talent/creators/${creatorId}/exclusivity`, { withCredentials: true });
      setData(data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [creatorId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/talent/creators/${creatorId}/exclusivity`, data, { withCredentials: true });
      toast.success('Exclusivity updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const addItem = (field, value, setInput) => {
    if (!value.trim()) return;
    setData({ ...data, [field]: [...(data[field] || []), value.trim()] });
    setInput('');
  };
  const removeItem = (field, idx) => {
    setData({ ...data, [field]: data[field].filter((_, i) => i !== idx) });
  };

  const expired = data.exclusivity_expiry_date && new Date(data.exclusivity_expiry_date) < new Date();

  return (
    <div className="space-y-5">
      <div className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-2">Brand Exclusivity</div>
        <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 mb-3">Creators cannot work with these competitor brands until expiry.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {(data.brand_exclusivity || []).map((b, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF451A]/15 border border-[#FF451A]/30 text-[#0F0F0F] dark:text-white text-sm" data-testid="brand-exclusivity-chip">
              {b}
              <button onClick={() => removeItem('brand_exclusivity', i)} className="text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF3B30]"><X size={12} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={brandInput} onChange={(e) => setBrandInput(e.target.value)} placeholder="e.g. Nykaa" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="brand-exclusivity-input" />
          <Button onClick={() => addItem('brand_exclusivity', brandInput, setBrandInput)} className="bg-white/10 hover:bg-white/15 text-[#0F0F0F] dark:text-white rounded-lg" data-testid="add-brand-exclusivity-button"><Plus size={14} /></Button>
        </div>
      </div>

      <div className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-2">Category Exclusivity</div>
        <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 mb-3">Lock the creator out of competing categories.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {(data.category_exclusivity || []).map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white text-sm" data-testid="category-exclusivity-chip">
              {c}
              <button onClick={() => removeItem('category_exclusivity', i)} className="text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF3B30]"><X size={12} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={catInput} onChange={(e) => setCatInput(e.target.value)} placeholder="e.g. beauty" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="category-exclusivity-input" />
          <Button onClick={() => addItem('category_exclusivity', catInput, setCatInput)} className="bg-white/10 hover:bg-white/15 text-[#0F0F0F] dark:text-white rounded-lg" data-testid="add-category-exclusivity-button"><Plus size={14} /></Button>
        </div>
      </div>

      <div className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-2">Expiry Date</div>
        <Input type="date" value={data.exclusivity_expiry_date || ''} onChange={(e) => setData({ ...data, exclusivity_expiry_date: e.target.value })} className="max-w-xs bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="exclusivity-expiry-input" />
        {expired && <p className="text-xs text-[#FFB800] mt-2">⚠ Expiry passed — exclusivity is no longer enforced.</p>}
      </div>

      <Button onClick={save} disabled={saving} className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-6" data-testid="save-exclusivity-button">
        <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save Exclusivity'}
      </Button>
    </div>
  );
};

const NegotiationsTab = ({ creatorId }) => {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ campaign_id: '', brand_offer: '', counter_offer: '', finalized_rate: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/talent/creators/${creatorId}/negotiations`, { withCredentials: true });
      setItems(data || []);
    } catch (e) { console.error(e); }
  };
  const fetchCampaigns = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/talent/creators/${creatorId}/campaigns`, { withCredentials: true });
      setCampaigns(data || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => {
    fetchItems();
    fetchCampaigns();
    /* eslint-disable-next-line */
  }, [creatorId]);

  const add = async () => {
    if (!form.campaign_id) {
      toast.error('Pick a campaign first');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/talent/creators/${creatorId}/negotiations`, {
        creator_id: creatorId,
        campaign_id: form.campaign_id,
        brand_offer: form.brand_offer ? parseInt(form.brand_offer, 10) : null,
        counter_offer: form.counter_offer ? parseInt(form.counter_offer, 10) : null,
        finalized_rate: form.finalized_rate ? parseInt(form.finalized_rate, 10) : null,
        notes: form.notes || null,
      }, { withCredentials: true });
      toast.success('Negotiation logged · pushed to Brand & Admin');
      setForm({ campaign_id: '', brand_offer: '', counter_offer: '', finalized_rate: '', notes: '' });
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4 space-y-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">Log a Negotiation Round</div>
        {campaigns.length === 0 ? (
          <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg p-3 text-xs text-[#FFB800]">
            ⚠ This creator has no campaigns yet. Negotiations attach to a campaign — once a brief is assigned, you can log offers here.
          </div>
        ) : (
          <>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-[0.2em] block mb-1 text-[#0F0F0F] dark:text-white">Campaign</label>
              <select
                value={form.campaign_id}
                onChange={(e) => {
                  const cid = e.target.value;
                  const selected = campaigns.find((c) => c.id === cid);
                  setForm((prev) => ({
                    ...prev,
                    campaign_id: cid,
                    // Auto-fill brand offer with the campaign budget so TM doesn't have to re-type it
                    brand_offer: selected && selected.budget != null ? String(selected.budget) : '',
                  }));
                }}
                className="w-full rounded-md px-3 py-2 text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="nego-campaign-select"
              >
                <option value="">— Select a campaign —</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title || c.name} · {c.brand_name || '—'} · {c.stage}{c.budget != null ? ` · ₹${Number(c.budget).toLocaleString('en-IN')}` : ''}
                  </option>
                ))}
              </select>
              {form.campaign_id && (() => {
                const sel = campaigns.find((c) => c.id === form.campaign_id);
                if (!sel) return null;
                return (
                  <div className="mt-2 flex items-center gap-2 text-[11px] font-mono text-[#6B6B6B] dark:text-zinc-500" data-testid="nego-brand-offer-hint">
                    <span className="text-[#A1A1AA] dark:text-zinc-600 uppercase tracking-[0.2em] text-[9px]">Brand Offer</span>
                    <span className="text-[#0F0F0F] dark:text-white">
                      {sel.budget != null ? `₹${Number(sel.budget).toLocaleString('en-IN')}` : '— not set on campaign —'}
                    </span>
                    <span className="text-zinc-700">·</span>
                    <span className="text-[#A1A1AA] dark:text-zinc-600">auto-filled below, editable</span>
                  </div>
                );
              })()}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Input type="number" value={form.brand_offer} onChange={(e) => setForm({ ...form, brand_offer: e.target.value })} placeholder="Brand offer ₹" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="nego-brand-offer" />
              <Input type="number" value={form.counter_offer} onChange={(e) => setForm({ ...form, counter_offer: e.target.value })} placeholder="Counter ₹" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="nego-counter-offer" />
              <Input type="number" value={form.finalized_rate} onChange={(e) => setForm({ ...form, finalized_rate: e.target.value })} placeholder="Finalized ₹ (optional)" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="nego-finalized" />
            </div>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notes (terms, exclusivity asks, payment milestones)" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="nego-notes" />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] font-mono text-[#A1A1AA] dark:text-zinc-600 uppercase tracking-[0.2em]">
                ⤴ Submits to the brand + admin in real-time
              </div>
              <Button onClick={add} disabled={saving} className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-5" data-testid="add-negotiation-button">{saving ? 'Saving…' : 'Add Round'}</Button>
            </div>
          </>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-black/[0.10] dark:border-white/10 rounded-xl text-[#6B6B6B] dark:text-zinc-500 italic">No negotiation history yet.</div>
      ) : (
        <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[#6B6B6B] dark:text-zinc-500 font-mono text-[10px] uppercase tracking-[0.2em]">
              <tr>
                <th className="text-left p-3">When</th>
                <th className="text-left p-3">Campaign</th>
                <th className="text-right p-3">Brand</th>
                <th className="text-right p-3">Counter</th>
                <th className="text-right p-3">Finalized</th>
                <th className="text-left p-3">Notes</th>
                <th className="text-left p-3">By</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="border-t border-black/[0.08] dark:border-white/5" data-testid="negotiation-row">
                  <td className="p-3 text-[#6B6B6B] dark:text-zinc-400 font-mono text-xs">{(() => { try { return formatDistanceToNow(new Date(n.created_at), { addSuffix: true }); } catch { return ''; } })()}</td>
                  <td className="p-3 text-[#0F0F0F] dark:text-white text-xs">{n.campaign_title || <span className="text-[#6B6B6B] dark:text-zinc-500 font-mono">{(n.campaign_id || '').slice(0, 8)}…</span>}</td>
                  <td className="p-3 text-right text-[#0F0F0F] dark:text-white">{n.brand_offer ? `₹${n.brand_offer.toLocaleString('en-IN')}` : '—'}</td>
                  <td className="p-3 text-right text-[#FFB800]">{n.counter_offer ? `₹${n.counter_offer.toLocaleString('en-IN')}` : '—'}</td>
                  <td className="p-3 text-right text-[#00C972] font-semibold">{n.finalized_rate ? `₹${n.finalized_rate.toLocaleString('en-IN')}` : '—'}</td>
                  <td className="p-3 text-[#6B6B6B] dark:text-zinc-400 max-w-xs truncate">{n.notes || '—'}</td>
                  <td className="p-3 text-[#6B6B6B] dark:text-zinc-500 text-xs">{n.created_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TASK_STATE_META = {
  pending: { label: 'Pending', color: '#999999' },
  in_progress: { label: 'In Progress', color: '#0055FF' },
  done: { label: 'Done', color: '#00C972' },
  blocked: { label: 'Blocked', color: '#FF3B30' },
};

const TasksTab = ({ creatorId }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', due_date: '' });
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/talent/creators/${creatorId}/tasks`, { withCredentials: true });
      setItems(data || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [creatorId]);

  const add = async () => {
    if (!form.title.trim()) {
      toast.error('Title required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/talent/creators/${creatorId}/tasks`, form, { withCredentials: true });
      toast.success('Task assigned');
      setForm({ title: '', description: '', due_date: '' });
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (taskId, status) => {
    try {
      await axios.patch(`${API_URL}/api/talent/tasks/${taskId}`, { status }, { withCredentials: true });
      fetchItems();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const remove = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API_URL}/api/talent/tasks/${taskId}`, { withCredentials: true });
      setItems(items.filter((i) => i.id !== taskId));
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4 space-y-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">Assign New Task</div>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Task title (e.g. Upload Q1 media kit)" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="task-title-input" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Description (optional)" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="task-desc-input" />
          <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="task-due-input" />
        </div>
        <Button onClick={add} disabled={saving} className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-5" data-testid="add-task-button">{saving ? 'Saving…' : 'Assign Task'}</Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-black/[0.10] dark:border-white/10 rounded-xl text-[#6B6B6B] dark:text-zinc-500 italic">No tasks yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((t) => {
            const meta = TASK_STATE_META[t.status] || TASK_STATE_META.pending;
            return (
              <div key={t.id} className="flex items-start gap-3 p-3 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl group" data-testid="task-row">
                <select
                  value={t.status}
                  onChange={(e) => updateStatus(t.id, e.target.value)}
                  className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] rounded-md px-2 py-1 text-xs text-[#0F0F0F] dark:text-white font-mono uppercase tracking-wider"
                  style={{ color: meta.color }}
                  data-testid={`task-status-${t.id}`}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="done">Done</option>
                  <option value="blocked">Blocked</option>
                </select>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0F0F0F] dark:text-white">{t.title}</div>
                  {t.description && <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-0.5">{t.description}</div>}
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA] dark:text-zinc-600 mt-1">
                    by {t.assigned_by_name} {t.due_date && `· due ${t.due_date}`}
                  </div>
                </div>
                <button onClick={() => remove(t.id)} className="opacity-0 group-hover:opacity-100 text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] transition-all" data-testid="delete-task-button"><Trash2 size={14} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CONTENT_CAT_META = {
  ugc: { label: 'UGC', color: '#FF451A' },
  top_posts: { label: 'Top Posts', color: '#0055FF' },
  brand_references: { label: 'Brand References', color: '#9B6EFF' },
  raw_files: { label: 'Raw Files', color: '#FFB800' },
};

const ContentLibraryTab = ({ creatorId }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ url: '', title: '', content_category: 'ugc', notes: '' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/talent/creators/${creatorId}/content`, { withCredentials: true });
      setItems(data || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [creatorId]);

  const add = async () => {
    if (!form.url.trim()) {
      toast.error('URL required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/talent/creators/${creatorId}/content/link`, form, { withCredentials: true });
      toast.success('Content added');
      setForm({ url: '', title: '', content_category: 'ugc', notes: '' });
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/talent/content/link/${id}`, { withCredentials: true });
      setItems(items.filter((i) => i.id !== id));
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const filtered = filter === 'all' ? items : items.filter((i) => i.content_category === filter);

  return (
    <div className="space-y-4">
      <div className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4 space-y-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">Add Content Link</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://instagram.com/p/..." className="md:col-span-2 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="content-url-input" />
          <select value={form.content_category} onChange={(e) => setForm({ ...form, content_category: e.target.value })} className="rounded-md px-3 text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="content-category-select">
            {Object.entries(CONTENT_CAT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </div>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. 2M-view fitness reel)" className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="content-title-input" />
        <Button onClick={add} disabled={saving} className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-5" data-testid="add-content-button">{saving ? 'Saving…' : 'Add to Library'}</Button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter('all')} className={`px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${filter === 'all' ? 'bg-white/10 border-black/[0.15] dark:border-white/20 text-[#0F0F0F] dark:text-white' : 'border-black/[0.08] dark:border-white/5 text-[#6B6B6B] dark:text-zinc-500 hover:text-white'}`} data-testid="content-filter-all">All ({items.length})</button>
        {Object.entries(CONTENT_CAT_META).map(([k, m]) => {
          const n = items.filter((i) => i.content_category === k).length;
          return <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${filter === k ? 'border-black/[0.15] dark:border-white/20 text-[#0F0F0F] dark:text-white' : 'border-black/[0.08] dark:border-white/5 text-[#6B6B6B] dark:text-zinc-500 hover:text-white'}`} style={filter === k ? { backgroundColor: `${m.color}20` } : {}} data-testid={`content-filter-${k}`}>{m.label} ({n})</button>;
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-black/[0.10] dark:border-white/10 rounded-xl text-[#6B6B6B] dark:text-zinc-500 italic">No content in this category yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((c) => {
            const meta = CONTENT_CAT_META[c.content_category] || CONTENT_CAT_META.ugc;
            return (
              <div key={c.id} className="flex items-start gap-3 p-3 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl group" data-testid="content-row">
                <Library size={16} className="shrink-0 mt-0.5" style={{ color: meta.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0F0F0F] dark:text-white truncate">{c.title || c.url}</div>
                  <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#FF451A] hover:underline truncate flex items-center gap-1"><ExternalLink size={10} />{c.url}</a>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: `${meta.color}20`, color: meta.color }}>{meta.label}</span>
                    <span className="text-[10px] text-[#A1A1AA] dark:text-zinc-600 font-mono">added by {c.added_by_name}</span>
                  </div>
                </div>
                <button onClick={() => remove(c.id)} className="opacity-0 group-hover:opacity-100 text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] transition-all" data-testid="delete-content-button"><Trash2 size={14} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NotesTab = ({ creatorId }) => {
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchNotes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/talent/creators/${creatorId}/notes`, { withCredentials: true });
      setNotes(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotes(); /* eslint-disable-next-line */ }, [creatorId]);

  const addNote = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/talent/creators/${creatorId}/notes`, { content: content.trim() }, { withCredentials: true });
      setContent('');
      fetchNotes();
      toast.success('Note added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add note');
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API_URL}/api/talent/notes/${noteId}`, { withCredentials: true });
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-4">
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          placeholder="e.g. Prefers skincare brands, avoids political content, usually negotiates 10% above quote. Fast 24h turnaround."
          className="mb-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
          data-testid="note-input"
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#6B6B6B] dark:text-zinc-500">🔒 Only visible to talent managers + admins</p>
          <Button onClick={addNote} disabled={saving || !content.trim()} className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-5" data-testid="add-note-button">
            {saving ? 'Adding…' : 'Add Note'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6 text-[#6B6B6B] dark:text-zinc-500">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-black/[0.10] dark:border-white/10 rounded-xl text-[#6B6B6B] dark:text-zinc-500 italic">No notes yet. Drop your first operational note above.</div>
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <div key={n.id} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4 group" data-testid="note-row">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF451A] to-[#9B6EFF] flex items-center justify-center text-xs font-bold text-[#0F0F0F] dark:text-white">{n.author_name?.[0]?.toUpperCase() || '?'}</div>
                  <div>
                    <div className="text-sm font-medium text-[#0F0F0F] dark:text-white">{n.author_name}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A1A1AA] dark:text-zinc-600">{(() => { try { return formatDistanceToNow(new Date(n.created_at), { addSuffix: true }); } catch { return ''; } })()}</div>
                  </div>
                </div>
                <button onClick={() => deleteNote(n.id)} className="opacity-0 group-hover:opacity-100 text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] transition-all" data-testid="delete-note-button">
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{n.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CampaignsTab = ({ creatorId }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/talent/creators/${creatorId}/campaigns`, { withCredentials: true });
        setCampaigns(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [creatorId]);

  if (loading) return <div className="text-center py-6 text-[#6B6B6B] dark:text-zinc-500">Loading…</div>;
  if (campaigns.length === 0) return <div className="text-center py-8 border-2 border-dashed border-black/[0.10] dark:border-white/10 rounded-xl text-[#6B6B6B] dark:text-zinc-500 italic">No campaigns yet for this creator.</div>;

  return (
    <div className="space-y-2">
      <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 mb-2">Read-only view of campaigns assigned to this creator.</p>
      {campaigns.map((c) => (
        <div key={c.id} className="flex items-center justify-between p-3 bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl" data-testid="campaign-row">
          <div>
            <div className="text-[#0F0F0F] dark:text-white font-medium">{c.title || c.brand_name}</div>
            <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono uppercase tracking-wider">{c.brand_name} · ₹{(c.budget || 0).toLocaleString('en-IN')}</div>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-black/[0.10] dark:border-white/10 text-zinc-300">{c.stage}</span>
        </div>
      ))}
    </div>
  );
};
